import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Download, CheckCircle, FileText, Calculator, Clock, Gift, Minus, Calendar, Upload, Save, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayrollDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isHR, isAdmin } = useAuth();
  
  const [payroll, setPayroll] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [netSalary, setNetSalary] = useState('');
  const [savingNetSalary, setSavingNetSalary] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchPayrollDetail = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payroll/${id}`);
      setPayroll(response.data);
      setNetSalary(response.data.net_salary || '');
      
      // Fetch employee details
      if (response.data.employee_id) {
        const empResponse = await axios.get(`${API}/employees/${response.data.employee_id}`);
        setEmployee(empResponse.data);
      }
    } catch (error) {
      console.error('Error fetching payroll:', error);
      toast.error(t('errorOccurred'));
      navigate('/payroll');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    fetchPayrollDetail();
  }, [fetchPayrollDetail]);

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/export/pdf/${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lohnabrechnung_${payroll.employee_name.replace(' ', '_')}_${payroll.period_start}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleSaveNetSalary = async () => {
    if (!netSalary || isNaN(parseFloat(netSalary))) {
      toast.error(t('pleaseEnterValidAmount'));
      return;
    }
    
    setSavingNetSalary(true);
    try {
      await axios.put(`${API}/payroll/${id}/net-salary`, {
        net_salary: parseFloat(netSalary)
      });
      toast.success(t('savedSuccessfully'));
      fetchPayrollDetail();
    } catch (error) {
      console.error('Error saving net salary:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSavingNetSalary(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['.pdf', '.png', '.jpg', '.jpeg'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExt)) {
      toast.error(t('invalidFileType'));
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/payroll/${id}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('documentUploaded'));
      fetchPayrollDetail();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDeleteDocument = async () => {
    if (!window.confirm(t('confirmDeleteDocument'))) return;
    
    try {
      await axios.delete(`${API}/payroll/${id}/document`);
      toast.success(t('documentDeleted'));
      fetchPayrollDetail();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleDownloadDocument = () => {
    if (payroll.final_document_url) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}${payroll.final_document_url}`, '_blank');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value || 0) + ' TND';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!payroll) return null;

  const hourlyRate = payroll.hourly_rate || employee?.hourly_rate || 0;
  const standardDailyHours = payroll.standard_daily_hours || employee?.standard_daily_hours || 8;
  const vacationHours = payroll.vacation_hours || (payroll.vacation_days * standardDailyHours);
  const overtimePay = payroll.overtime_pay || 0;
  
  // Berechne totalDeductions korrekt (auch wenn DB-Wert falsch ist)
  const advanceDeduction = payroll.advance_deduction || 0;
  const loanDeduction = payroll.loan_deduction || 0;
  const recurringDeductions = payroll.recurring_deductions || 0;
  const totalDeductions = advanceDeduction + loanDeduction + recurringDeductions;
  
  // Berechne totalBonus korrekt (auch wenn DB-Wert null ist)
  const standardBonus = payroll.standard_bonus || 0;
  const oneTimeBonus = payroll.one_time_bonus || 0;
  const commission = payroll.commission || 0;
  const specialPayments = payroll.special_payments || 0;
  const recurringAdditions = payroll.recurring_additions || 0;
  const totalBonus = payroll.total_bonus ?? (standardBonus + oneTimeBonus + commission + specialPayments);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payroll-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/payroll')}
            className="text-gray-500 hover:text-[#1A1A1A]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">
              {payroll.employee_name}
            </h1>
            <p className="text-gray-500 text-sm">
              {payroll.period_start} - {payroll.period_end}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {payroll.status === 'finalized' ? (
            <Badge className="badge-success">
              <CheckCircle className="w-3 h-3 mr-1" />
              {t('finalized')}
            </Badge>
          ) : (
            <Badge className="badge-warning">
              <FileText className="w-3 h-3 mr-1" />
              {t('draft')}
            </Badge>
          )}
          <Button
            onClick={handleDownloadPdf}
            className="bg-[#E31E24] hover:bg-[#C41218] text-white"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Employee Info */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-[#1A1A1A]">Mitarbeiterdaten</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Mitarbeiter-ID</p>
              <p className="font-mono text-[#1A1A1A]">{payroll.employee_number || employee?.employee_number || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Sozialversicherung</p>
              <p className="font-mono text-[#1A1A1A]">{employee?.social_security_number || payroll.employee_ssn || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Stundenlohn</p>
              <p className="font-mono text-[#1A1A1A] font-semibold">{formatCurrency(hourlyRate)}</p>
            </div>
            <div>
              <p className="text-gray-500">Standard Tagesstunden</p>
              <p className="font-mono text-[#1A1A1A]">{standardDailyHours} h</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Calculations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* 1. Grundlohn */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3 bg-red-50 rounded-t-lg">
            <CardTitle className="text-base text-[#E31E24] flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              1. Grundlohn / Salaire de base
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {payroll.is_fixed_salary ? (
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#1A1A1A]">Monatliches Fixgehalt</p>
                    <p className="text-xs text-gray-400">Salaire fixe mensuel</p>
                  </div>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.base_gross)}</p>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#1A1A1A]">Reguläre Arbeitsstunden</p>
                    <p className="text-xs text-gray-400">
                      {payroll.regular_hours} h × {formatCurrency(hourlyRate)}
                    </p>
                  </div>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.base_gross)}</p>
                </div>
              )}
              
              <Separator />
              <div className="flex justify-between items-center font-semibold">
                <p className="text-[#1A1A1A]">Grundlohn</p>
                <p className="font-mono text-[#E31E24]">{formatCurrency(payroll.base_gross)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Zuschläge (inkl. Überstunden) */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3 bg-gray-100 rounded-t-lg">
            <CardTitle className="text-base text-gray-700 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              2. Zuschläge & Überstunden / Primes
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {payroll.overtime_hours > 0 && (
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#1A1A1A]">Überstunden (+{payroll.overtime_surcharge_percent || 25}%)</p>
                    <p className="text-xs text-gray-400">
                      {payroll.overtime_hours} h × {formatCurrency(hourlyRate)} × {(1 + (payroll.overtime_surcharge_percent || 25)/100).toFixed(2)}
                    </p>
                  </div>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(overtimePay)}</p>
                </div>
              )}
              
              {(payroll.night_hours || 0) > 0 && (
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#1A1A1A]">Nachtstunden (+{payroll.night_surcharge_percent || 0}%)</p>
                    <p className="text-xs text-gray-400">
                      {payroll.night_hours} h × {formatCurrency(hourlyRate)} × {(1 + (payroll.night_surcharge_percent || 0)/100).toFixed(2)}
                    </p>
                  </div>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.night_surcharge || 0)}</p>
                </div>
              )}
              
              {payroll.holiday_hours > 0 && (
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#1A1A1A]">Feiertagsstunden (+{payroll.holiday_surcharge_percent || 0}%)</p>
                    <p className="text-xs text-gray-400">
                      {payroll.holiday_hours} h × {formatCurrency(hourlyRate)} × {(1 + (payroll.holiday_surcharge_percent || 0)/100).toFixed(2)}
                    </p>
                  </div>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.holiday_surcharge)}</p>
                </div>
              )}
              
              {payroll.weekend_hours > 0 && (
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#1A1A1A]">Wochenendstunden (+{payroll.weekend_surcharge_percent || 0}%)</p>
                    <p className="text-xs text-gray-400">
                      {payroll.weekend_hours} h × {formatCurrency(hourlyRate)} × {(1 + (payroll.weekend_surcharge_percent || 0)/100).toFixed(2)}
                    </p>
                  </div>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.weekend_surcharge)}</p>
                </div>
              )}
              
              {payroll.other_surcharges > 0 && (
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#1A1A1A]">Sonstige Zuschläge ({payroll.other_surcharge_percent || 0}%)</p>
                    <p className="text-xs text-gray-400">
                      {payroll.regular_hours + payroll.overtime_hours} h × {formatCurrency(hourlyRate)} × {((payroll.other_surcharge_percent || 0)/100).toFixed(2)}
                    </p>
                  </div>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.other_surcharges)}</p>
                </div>
              )}
              
              {payroll.total_surcharges === 0 && overtimePay === 0 && (
                <p className="text-gray-400 text-sm">Keine Zuschläge</p>
              )}
              
              <Separator />
              <div className="flex justify-between items-center font-semibold">
                <p className="text-[#1A1A1A]">Gesamt Zuschläge</p>
                <p className="font-mono text-gray-700">{formatCurrency(payroll.total_surcharges + overtimePay)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Urlaub */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3 bg-green-50 rounded-t-lg">
            <CardTitle className="text-base text-green-700 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              3. Urlaub / Congés
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {payroll.vacation_days > 0 ? (
                <>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[#1A1A1A]">Urlaubstage</p>
                      <p className="text-xs text-gray-400">
                        {payroll.vacation_days} Tage × {standardDailyHours} h = {vacationHours} h
                      </p>
                    </div>
                    <p className="font-mono text-[#1A1A1A]">{payroll.vacation_days} Tage</p>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[#1A1A1A]">Urlaubsgeld</p>
                      <p className="text-xs text-gray-400">
                        {vacationHours} h × {formatCurrency(hourlyRate)}
                      </p>
                    </div>
                    <p className="font-mono font-semibold text-green-600">{formatCurrency(payroll.vacation_pay)}</p>
                  </div>
                </>
              ) : (
                <p className="text-gray-400 text-sm">Kein Urlaub in diesem Zeitraum</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 4. Bonus & Extras */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3 bg-blue-50 rounded-t-lg">
            <CardTitle className="text-base text-blue-700 flex items-center gap-2">
              <Gift className="w-5 h-5" />
              4. Bonus & Extras
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {(payroll.standard_bonus || 0) > 0 && (
                <div className="flex justify-between items-center">
                  <p className="text-[#1A1A1A]">Standard Bonus</p>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.standard_bonus)}</p>
                </div>
              )}
              {payroll.one_time_bonus > 0 && (
                <div className="flex justify-between items-center">
                  <p className="text-[#1A1A1A]">Einmalbonus</p>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.one_time_bonus)}</p>
                </div>
              )}
              {payroll.commission > 0 && (
                <div className="flex justify-between items-center">
                  <p className="text-[#1A1A1A]">Provision</p>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.commission)}</p>
                </div>
              )}
              {payroll.special_payments > 0 && (
                <div className="flex justify-between items-center">
                  <p className="text-[#1A1A1A]">Sonderzahlungen</p>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.special_payments)}</p>
                </div>
              )}
              {payroll.recurring_additions > 0 && (
                <div className="flex justify-between items-center">
                  <p className="text-[#1A1A1A]">Wiederkehrende Zulagen</p>
                  <p className="font-mono font-semibold text-[#1A1A1A]">{formatCurrency(payroll.recurring_additions)}</p>
                </div>
              )}
              
              {payroll.total_bonus === 0 && payroll.recurring_additions === 0 && oneTimeBonus === 0 && commission === 0 && specialPayments === 0 && (
                <p className="text-gray-400 text-sm">Keine Bonus-Zahlungen</p>
              )}
              
              <Separator />
              <div className="flex justify-between items-center font-semibold">
                <p className="text-[#1A1A1A]">Gesamt Bonus</p>
                <p className="font-mono text-blue-600">{formatCurrency(totalBonus + recurringAdditions)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 5. Abzüge */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-3 bg-red-100 rounded-t-lg">
          <CardTitle className="text-base text-red-700 flex items-center gap-2">
            <Minus className="w-5 h-5" />
            5. Abzüge / Déductions
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {payroll.advance_deduction > 0 && (
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-md">
                <p className="text-[#1A1A1A]">Vorschuss</p>
                <p className="font-mono font-semibold text-red-600">-{formatCurrency(payroll.advance_deduction)}</p>
              </div>
            )}
            {payroll.loan_deduction > 0 && (
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-md">
                <p className="text-[#1A1A1A]">Kredit</p>
                <p className="font-mono font-semibold text-red-600">-{formatCurrency(payroll.loan_deduction)}</p>
              </div>
            )}
            {payroll.recurring_deductions > 0 && (
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-md">
                <p className="text-[#1A1A1A]">Wiederkehrende Abzüge</p>
                <p className="font-mono font-semibold text-red-600">-{formatCurrency(payroll.recurring_deductions)}</p>
              </div>
            )}
            {totalDeductions === 0 && (
              <p className="text-gray-400 text-sm">Keine Abzüge</p>
            )}
          </div>
          {totalDeductions > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
              <div className="text-right">
                <p className="text-sm text-gray-500">Gesamt Abzüge</p>
                <p className="font-mono text-xl font-bold text-red-600">-{formatCurrency(totalDeductions)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-white border-2 border-[#E31E24]">
        <CardHeader className="pb-3 bg-gradient-to-r from-[#E31E24] to-[#C41218] rounded-t-lg">
          <CardTitle className="text-lg text-white">ZUSAMMENFASSUNG / RÉSUMÉ</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-gray-600">Grundlohn</p>
              <p className="font-mono text-[#1A1A1A]">{formatCurrency(payroll.base_gross)}</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-600">+ Zuschläge & Überstunden</p>
              <p className="font-mono text-[#1A1A1A]">{formatCurrency(payroll.total_surcharges + overtimePay)}</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-600">+ Urlaubsgeld</p>
              <p className="font-mono text-[#1A1A1A]">{formatCurrency(payroll.vacation_pay)}</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-600">+ Bonus & Zulagen</p>
              <p className="font-mono text-[#1A1A1A]">{formatCurrency(totalBonus + recurringAdditions)}</p>
            </div>
            
            <Separator className="my-4" />
            
            {/* GESAMT BRUTTO - Hauptbetrag */}
            <div className="flex justify-between items-center bg-[#E31E24] p-4 rounded-md -mx-2">
              <p className="font-bold text-white text-lg">= GESAMT BRUTTO</p>
              <p className="font-mono text-2xl font-bold text-white">{formatCurrency(payroll.total_gross)}</p>
            </div>
            
            {/* Hinweis auf Abzüge vom Netto */}
            {totalDeductions > 0 && (
              <div className="mt-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="bg-amber-400 text-white rounded-full p-1 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-amber-800 text-sm">ABZÜGE VOM NETTO-LOHN</p>
                    <p className="text-amber-700 text-xs mt-1">
                      Folgende Beträge sind vom Netto-Lohn abzuziehen:
                    </p>
                    <div className="mt-2 space-y-1">
                      {advanceDeduction > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-amber-700">Vorschuss:</span>
                          <span className="font-mono font-semibold text-amber-900">{formatCurrency(advanceDeduction)}</span>
                        </div>
                      )}
                      {loanDeduction > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-amber-700">Kredit:</span>
                          <span className="font-mono font-semibold text-amber-900">{formatCurrency(loanDeduction)}</span>
                        </div>
                      )}
                      {recurringDeductions > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-amber-700">Wiederkehrende Abzüge:</span>
                          <span className="font-mono font-semibold text-amber-900">{formatCurrency(recurringDeductions)}</span>
                        </div>
                      )}
                      <Separator className="my-2" />
                      <div className="flex justify-between text-sm font-bold">
                        <span className="text-amber-800">GESAMT ABZÜGE:</span>
                        <span className="font-mono text-amber-900">{formatCurrency(totalDeductions)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <p className="text-xs text-gray-400 mt-6 text-center">
            * Netto-Berechnung erfolgt durch den Steuerberater
          </p>
        </CardContent>
      </Card>

      {/* Tax Advisor Section - Only for finalized payrolls */}
      {payroll.status === 'finalized' && (
        <Card className="bg-white border-2 border-blue-500" data-testid="tax-advisor-section">
          <CardHeader className="pb-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-t-lg">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              {t('taxAdvisorSection')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Net Salary Input */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-blue-500" />
                  {t('netSalary')}
                </h3>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Input
                      type="number"
                      step="0.001"
                      value={netSalary}
                      onChange={(e) => setNetSalary(e.target.value)}
                      placeholder={t('enterNetSalary')}
                      className="bg-white border-gray-200 text-[#1A1A1A] font-mono"
                      data-testid="net-salary-input"
                    />
                  </div>
                  {isHR() && (
                    <Button
                      onClick={handleSaveNetSalary}
                      disabled={savingNetSalary}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                      data-testid="save-net-salary-btn"
                    >
                      {savingNetSalary ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
                {payroll.net_salary && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-700">{t('finalPayout')}:</span>
                      <span className="font-mono font-bold text-green-800 text-lg">
                        {formatCurrency(payroll.net_salary)}
                      </span>
                    </div>
                    {payroll.net_salary_updated_by && (
                      <p className="text-xs text-green-600 mt-1">
                        {t('updatedBy')}: {payroll.net_salary_updated_by} 
                        {payroll.net_salary_updated_at && ` (${new Date(payroll.net_salary_updated_at).toLocaleDateString('de-DE')})`}
                      </p>
                    )}
                    <p className="text-xs text-green-600 mt-2 italic">
                      {t('netSalaryIncludesDeductions')}
                    </p>
                  </div>
                )}
              </div>

              {/* Document Upload */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  {t('finalDocument')}
                </h3>
                
                {payroll.final_document_url ? (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <FileText className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{payroll.final_document_name}</p>
                          <p className="text-xs text-gray-500">
                            {t('uploadedBy')}: {payroll.final_document_uploaded_by}
                            {payroll.final_document_uploaded_at && ` (${new Date(payroll.final_document_uploaded_at).toLocaleDateString('de-DE')})`}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadDocument}
                          className="text-blue-600 hover:text-blue-700"
                          data-testid="download-document-btn"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        {isAdmin() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDeleteDocument}
                            className="text-red-600 hover:text-red-700"
                            data-testid="delete-document-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-3">{t('uploadFinalDocument')}</p>
                    {isHR() && (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={handleFileUpload}
                          className="hidden"
                          data-testid="file-upload-input"
                        />
                        <Button
                          variant="outline"
                          disabled={uploading}
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          asChild
                        >
                          <span>
                            {uploading ? (
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            {t('selectFile')}
                          </span>
                        </Button>
                      </label>
                    )}
                    <p className="text-xs text-gray-400 mt-2">PDF, PNG, JPG (max. 10MB)</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
